import {
	Box,
	Button,
	Divider,
	Heading,
	Icons,
	Inline,
	RelativeTime,
	Spinner,
	Stack,
	Text,
	joinAttributes,
	Alert,
	useId,
	Grid,
	Col,
} from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { Markdown } from "@sembark-travel/ui-markdown"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { TBrandWiseColors, TTripQuotePdfTemplate } from "./store"
import { isTruthy } from "@sembark-travel/ui-form"
import { FontFamily } from "./FontFamily"
import {
	DaywisePreview,
	EditTripQuotePdfTemplateForm,
	HotelPreview,
	PackagePreviewDefaultView,
	PackagePreviewTableView,
	TEmailPdfTemplateCreatePayload,
} from "./EditTripQuotePdfTemplateForm"
import { hexToRgba } from "../utils"

export function TripQuotePdfTemplates() {
	const xhr = useXHR()
	const { data, mutate, error } = useSWR("/trip-quote-pdf-templates", () =>
		xhr
			.get<{
				data: Array<TTripQuotePdfTemplate>
				meta: TBrandWiseColors
			}>("/trip-quote-pdf-templates")
			.then((resp) => resp.data)
	)
	const { hasPermission } = useCheckPermissions()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	if (!data) {
		if (error) {
			return (
				<Alert status="error">
					{error.message || "Something went wrong. Please try after sometime."}
				</Alert>
			)
		}
		return <Spinner alignCenter padding="4" />
	}
	const dark_fill =
		data.meta.brand_wise_color_shades[0].color_shades?.["shade-700"]
	const borderColor =
		data.meta.brand_wise_color_shades[0].color_shades?.["shade-600"] ||
		"emphasis"
	const light_fill =
		data.meta.brand_wise_color_shades[0].color_shades?.["shade-200"]
	const textColor =
		data.meta.brand_wise_color_shades[0].color_shades?.["shade-800"]
	return (
		<Stack gap="4">
			<Inline justifyContent="between" gap="4" alignItems="center">
				<Inline gap="4" alignItems="center">
					<Inline
						size="12"
						rounded="full"
						bgColor="default"
						borderWidth="1"
						alignItems="center"
						justifyContent="center"
						color="muted"
						fontFamily="mono"
					>
						PDF
					</Inline>
					<Stack gap="1">
						<Heading as="h3">Quote Pdf Template Configurations</Heading>
						<Text>These configuration will be applied to Quote PDFs only.</Text>
					</Stack>
				</Inline>
				{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
					<Box>
						<Button
							onClick={openDialog}
							level={!data.data.length ? "primary" : "secondary"}
							status="primary"
							size="sm"
						>
							<Icons.Pencil /> Edit
						</Button>
					</Box>
				) : null}
			</Inline>
			<Divider marginY="0" />
			<Box>
				{!data.data.length ? (
					<Box padding="4" bgColor="default" rounded="md" borderWidth="1">
						<Text>Using default quote pdf template configurations.</Text>
					</Box>
				) : (
					data.data.map((template) => {
						return (
							<Stack key={template.id} gap="4">
								<Text>
									<b>Font Family</b>: {template.settings.fontFamily.name}
								</Text>
								<Text>
									<b>Heading Font Family</b>:{" "}
									{template.settings.headingFontFamily.name}
								</Text>
								<Text>
									<b>Justified Text</b>:{" "}
									{isTruthy(template.settings.justifiedText)
										? "Justified"
										: "Default"}
								</Text>
								<Text>
									<b>Watermark Text</b>:{" "}
									{template.settings.watermarkText ? (
										template.settings.watermarkText
									) : (
										<Text as="span" color={"muted"}>
											Disabled
										</Text>
									)}
								</Text>
								<Text>
									<b>Hide Branding For B2B</b>:{" "}
									{isTruthy(template.settings.hideBrandingB2B) ? (
										<>
											Yes <Icons.OkCircle color={"success"} size={"6"} />
										</>
									) : (
										<>
											No <Icons.XCircle color={"danger"} size={"6"} />
										</>
									)}
								</Text>
								<Text>
									<b>Hide Branding For B2C</b>:{" "}
									{isTruthy(template.settings.hideBrandingB2C) ? (
										<>
											Yes <Icons.OkCircle color={"success"} size={"6"} />
										</>
									) : (
										<>
											No <Icons.XCircle color={"danger"} size={"6"} />
										</>
									)}
								</Text>
								<Text>
									<b>Header Banner Image</b>:{" "}
									{isTruthy(template.settings.showHeaderBannerImg) ? (
										<>
											Show <Icons.OkCircle color={"success"} size={"6"} />
										</>
									) : (
										<>
											Hide <Icons.XCircle color={"danger"} size={"6"} />
										</>
									)}
								</Text>
								<Text>
									<b>Footer Banner Image</b>:{" "}
									{isTruthy(template.settings.showFooterBannerImg) ? (
										<>
											Show <Icons.OkCircle color={"success"} size={"6"} />
										</>
									) : (
										<>
											Hide <Icons.XCircle color={"danger"} size={"6"} />
										</>
									)}
								</Text>
								<Text>
									<b>Show Header on First Page & Footer on Last Page</b>:{" "}
									{isTruthy(template.settings.showSingleHeaderFooter) ? (
										<>
											Yes <Icons.OkCircle color={"success"} size={"6"} />
										</>
									) : (
										<>
											No <Icons.XCircle color={"danger"} size={"6"} />
										</>
									)}
								</Text>
								{/* watermark opacity */}
								<Inline gap="4" alignItems="center">
									<b>Watermark Opacity</b>:{" "}
									<Inline
										paddingX="4"
										paddingY="4"
										rounded="md"
										borderWidth="1"
										flexWrap="wrap"
										gap="4"
										style={{
											backgroundImage:
												"radial-gradient(rgb(68, 76, 247) 0.5px, rgb(255, 255, 255) 0.5px)",
											backgroundSize: "10px 10px",
										}}
									>
										<div
											style={{
												display: "inline-block",
												width: "100px",
												height: "30px",
												background: "black",
												opacity: template.settings.watermarkOpacity,
											}}
										></div>
										<div
											style={{
												display: "inline-block",
												width: "100px",
												height: "30px",
												background: "black",
												opacity: template.settings.watermarkOpacity,
											}}
										></div>
										<div
											style={{
												display: "inline-block",
												width: "100px",
												height: "30px",
												background: "black",
												opacity: template.settings.watermarkOpacity,
											}}
										></div>
									</Inline>
								</Inline>
								<Text>
									<b>Border Style,Width and backgroundOpacity </b>:{" "}
									{template.settings.borderStyle &&
									template.settings.borderStyle !== "none" ? (
										<Text
											as="span"
											style={{
												padding: "0 4px",
												display: "inline-block",
												backgroundColor: hexToRgba(
													data.meta.brand_wise_color_shades[0].color_shades?.[
														"shade-50"
													] || "transparent",
													template.settings.backgroundOpacity || 0
												),
												borderColor:
													data.meta.brand_wise_color_shades[0].color_shades?.[
														"shade-400"
													] || "silver",
												borderStyle: template.settings.borderStyle,
												borderWidth:
													[
														template.settings.borderTopWidth || 0,
														template.settings.borderRightWidth || 0,
														template.settings.borderBottomWidth || 0,
														template.settings.borderLeftWidth || 0,
													].join("px ") + "px",
											}}
										>
											{template.settings.borderStyle} • Top:
											{template.settings.borderTopWidth || 0}px • Right:
											{template.settings.borderRightWidth || 0}px • Bottom:
											{template.settings.borderBottomWidth || 0}px • Left:
											{template.settings.borderLeftWidth || 0}px • Opacity:
											{(template.settings.backgroundOpacity || 0) * 100}
										</Text>
									) : (
										<Text as="span" color={"muted"}>
											none
										</Text>
									)}
								</Text>
								{template.section_title_settings ? (
									<Stack gap="1">
										<Text>
											<b>Section Title Settings:</b>{" "}
										</Text>
										<Box
											padding="4"
											rounded="md"
											bgColor="default"
											borderWidth="1"
										>
											<Inline gap="4" flexWrap="wrap" alignItems="center">
												<Box>
													{joinAttributes(
														`Background: ${template.section_title_settings.background}`,
														`Border: ${template.section_title_settings.border}`,
														`Rounded: ${template.section_title_settings.rounded}`,
														`Text Align: ${template.section_title_settings.textAlign}`
													)}
												</Box>
												<Box
													fontSize="sm"
													paddingX="4"
													paddingY="2"
													fontWeight="semibold"
													roundedRight={
														template.section_title_settings.rounded === "right"
															? "md"
															: undefined
													}
													roundedLeft={
														template.section_title_settings.rounded === "left"
															? "md"
															: undefined
													}
													roundedBottom={
														template.section_title_settings.rounded === "bottom"
															? "md"
															: undefined
													}
													roundedTop={
														template.section_title_settings.rounded === "top"
															? "md"
															: undefined
													}
													rounded={
														template.section_title_settings.rounded === "all"
															? "md"
															: undefined
													}
													style={{
														borderColor: borderColor,
														color:
															template.section_title_settings.background ===
															"dark_fill"
																? "#fff"
																: textColor,
														backgroundColor:
															template.section_title_settings.background ===
															"fill"
																? light_fill
																: template.section_title_settings.background ===
																	  "dark_fill"
																	? dark_fill
																	: "#fff",
													}}
													borderLeftWidth={
														template.section_title_settings.border === "left" ||
														template.section_title_settings.border === "all" ||
														template.section_title_settings.border ===
															"vertical"
															? "4"
															: "0"
													}
													borderRightWidth={
														template.section_title_settings.border ===
															"right" ||
														template.section_title_settings.border === "all" ||
														template.section_title_settings.border ===
															"vertical"
															? "4"
															: "0"
													}
													borderTopWidth={
														template.section_title_settings.border === "top" ||
														template.section_title_settings.border === "all" ||
														template.section_title_settings.border ===
															"horizontal"
															? "4"
															: "0"
													}
													borderBottomWidth={
														template.section_title_settings.border ===
															"bottom" ||
														template.section_title_settings.border === "all" ||
														template.section_title_settings.border ===
															"horizontal"
															? "4"
															: "0"
													}
												>
													Sample Title Text
												</Box>
											</Inline>
											<Text>
												Section Heading Font Family:{" "}
												{
													template.section_title_settings
														.sectionHeadingFontFamily.name
												}
											</Text>
										</Box>
									</Stack>
								) : null}

								<Stack gap="1">
									<Text fontWeight="semibold">
										<Icons.Bed /> Settings for Hotels Section
									</Text>
									<Box
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="4">
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Section Title
												</Text>
												<Text>
													{template.hotel_section_settings?.title || "N/A"}
												</Text>
											</Stack>
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Similar Word In Hotel Names
												</Text>
												<Text>
													{isTruthy(
														template.hotel_section_settings
															?.similar_word_in_hotel
													)
														? "Enabled - Will show (Similar) word in Hotel Names - Example -> Taj Hotel / Similar"
														: "Disabled - Will not show (Similar) word in Hotel Names"}
												</Text>
											</Stack>
											<Stack gap="2">
												<Box>
													<Text
														fontSize="sm"
														fontWeight="semibold"
														color="muted"
													>
														Hotel Card Style
													</Text>
													<Text>
														{template.hotel_section_settings?.variant.name}
														{template.hotel_section_settings?.variant?.id ===
															"image_and_text" &&
															template.hotel_section_settings?.variant
																.configurations[0].value &&
															` (${
																template.hotel_section_settings.variant
																	.configurations[0].value
															})`}
													</Text>
												</Box>
												<Grid>
													<Col paddingX="0" sm={12} md={6}>
														<Box padding="1" rounded="md" borderWidth="1">
															{template.hotel_section_settings?.variant.id ===
																"image_and_text" &&
															template.hotel_section_settings.variant ? (
																<HotelPreview
																	alignment={
																		template.hotel_section_settings.variant
																			.configurations[0].value || "left"
																	}
																/>
															) : template.hotel_section_settings?.variant
																	.id === "table_view" ? (
																<HotelPreview alignment="table_view" />
															) : null}
														</Box>
													</Col>
												</Grid>
											</Stack>
										</Stack>
									</Box>
								</Stack>

								<Stack gap="1">
									<Text fontWeight="semibold">Greetings:</Text>
									<FontFamily family={template.settings.fontFamily}>
										<Box
											style={{
												textAlign: isTruthy(template.settings.justifiedText)
													? "justify"
													: undefined,
											}}
											paddingX="4"
											paddingY="2"
											borderWidth="1"
											rounded="md"
											bgColor="default"
										>
											<Markdown>{template.greetings}</Markdown>
										</Box>
									</FontFamily>
								</Stack>
								<Stack gap="1">
									<Text fontWeight="semibold">Package Preview</Text>
									<Box
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="2">
											<Box>
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Package Preview Style
												</Text>
												<Text>{template.package_preview.variant.name}</Text>
											</Box>
											<Grid>
												<Col paddingX="0" sm={12} md={6}>
													<Box>
														{template.package_preview.variant.id ===
															"show_details_in_table" && (
															<PackagePreviewTableView />
														)}
														{template.package_preview.variant.id ===
															"default" && <PackagePreviewDefaultView />}
													</Box>
												</Col>
											</Grid>
										</Stack>
									</Box>
								</Stack>
								<Stack gap="1">
									<Text fontWeight="semibold">
										Settings for Transportation and Activity Section
									</Text>
									<Box
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="2">
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Section Title
												</Text>
												<Text>
													{template.transport_and_activity_section_settings
														.title || "Using default settings"}
												</Text>
											</Stack>
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Section Variant
												</Text>
												<Text>
													{
														template.transport_and_activity_section_settings
															.variant
													}
												</Text>
											</Stack>
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Hide Transport Summary if Same Cab Type is used?
												</Text>
												<Text>
													{isTruthy(
														template.transport_and_activity_section_settings
															.hide_transport_summary_for_same_cabs
													)
														? "Yes, Hide Transport Summary"
														: "No, Show Day Wise Transport Summary"}
												</Text>
											</Stack>
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Specific Terms and Notes
												</Text>
												<Markdown>
													{template.transport_and_activity_section_settings
														.terms || "N/A"}
												</Markdown>
											</Stack>
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Show Activity Start Time
												</Text>
												<Text>
													{isTruthy(
														template.transport_and_activity_section_settings
															.show_activity_start_time
													)
														? "Yes, Show Activity Start Time"
														: "No, Hide Activity Start Time"}
												</Text>
											</Stack>
										</Stack>
									</Box>
								</Stack>
								<Stack gap="1">
									<Text fontWeight="semibold">
										Settings for Pricing Section
									</Text>
									<Box
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="2">
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Package Price Location
												</Text>
												<Text>
													{isTruthy(
														template.pricing_section_settings
															?.show_package_price_below_hotel
													)
														? "Package Price will be shown with option-wise hotels"
														: "Package Price will be shown in a separate Total Price section"}
												</Text>
											</Stack>
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Hide Price in Quote
												</Text>
												<Text>
													{isTruthy(
														template.pricing_section_settings?.hide_quote_price
													)
														? "Enabled - Will hide the price in the Package"
														: "Disabled - Will show the price in the Package"}
												</Text>
											</Stack>
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Show Price Breakup (Component wise Total)
												</Text>
												<Text>
													{isTruthy(
														template.pricing_section_settings
															?.show_component_wise_prices
													)
														? "Enabled - Will show the component wise price breakup in the Package"
														: "Disabled - Will not show the component wise price breakup in the Package"}
												</Text>
											</Stack>
										</Stack>
									</Box>
								</Stack>
								<Stack gap="1">
									<Text fontWeight="semibold">
										<Icons.SwitchHorizontal rotate="90" /> Day Wise Itinerary
										Settings
									</Text>
									<Stack
										gap="4"
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Section Title
											</Text>
											<Text>
												{template.daywise_schedule_section_settings?.title ||
													"N/A"}
											</Text>
										</Stack>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Travel Distance
											</Text>
											<Text>
												{isTruthy(
													template.daywise_schedule_section_settings
														?.hide_travel_distance
												)
													? "Always Hide"
													: "Always Show"}
											</Text>
										</Stack>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Show Services Together
											</Text>
											<Text>
												{isTruthy(
													template.daywise_schedule_section_settings
														?.show_same_day_service_together
												)
													? "Enabled - Will show services together in the same day"
													: "Disabled - Will not show services together in the same day"}
											</Text>
										</Stack>
										<Stack gap="2">
											<Box>
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Itinerary Style
												</Text>
												<Text>
													{
														template.daywise_schedule_section_settings?.variant
															.name
													}{" "}
													(
													{
														template.daywise_schedule_section_settings?.variant
															.configurations[0].value
													}
													)
												</Text>
											</Box>
											<Grid>
												<Col paddingX="0" sm={12} md={6}>
													<Stack padding="2" rounded="md" borderWidth="1">
														<DaywisePreview
															alignment={
																template.daywise_schedule_section_settings
																	?.variant.configurations[0].value ||
																"align_to_service"
															}
															servicesTogether={Boolean(
																Number(
																	template.daywise_schedule_section_settings
																		?.show_same_day_service_together
																)
															)}
														/>
													</Stack>
												</Col>
											</Grid>
										</Stack>
									</Stack>
								</Stack>
								{template.visa_section_settings ? (
									<Stack gap="1">
										<Text fontWeight="semibold">
											<Icons.Passport /> Visa Settings
										</Text>
										<Stack
											gap="4"
											padding="4"
											rounded="md"
											bgColor="default"
											borderWidth="1"
										>
											<Stack gap="px">
												<Text fontSize="sm" fontWeight="semibold" color="muted">
													Section Title
												</Text>
												<Text>
													{template.visa_section_settings?.title ?? "N/A"}
												</Text>
											</Stack>
										</Stack>
									</Stack>
								) : null}
								<Stack gap={"1"}>
									<Text fontWeight="semibold">
										<Icons.SwitchHorizontal rotate="90" />
										Show, Hide & Reorder PDF Sections
									</Text>
									<Stack
										gap="4"
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="2">
											{template.section_ordering_settings.map(
												(section, index) =>
													section.hide === 0 && (
														<Inline gap="2" alignItems={"center"} key={index}>
															<Stack
																size={"6"}
																alignItems="center"
																justifyContent="center"
																borderWidth="1"
																rounded="full"
																bgColor="success_emphasis"
																color="on_emphasis"
															>
																<Text fontWeight="semibold">{index + 1}</Text>
															</Stack>
															<Box flex="1" minWidth="0">
																<Box
																	paddingX={"4"}
																	paddingY={"2"}
																	borderWidth="1"
																	rounded="md"
																	maxWidth={"xs"}
																>
																	<Inline
																		gap="4"
																		alignItems={"center"}
																		justifyContent="between"
																	>
																		<Text>{section.title}</Text>
																	</Inline>
																</Box>
															</Box>
														</Inline>
													)
											)}
										</Stack>
									</Stack>
								</Stack>
								<Stack gap="1">
									<Text fontWeight="semibold">
										<Icons.Chat /> Important Notes
									</Text>
									<Stack
										gap="4"
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Section Title
											</Text>
											<Text>{template.other_info_section_settings?.title}</Text>
										</Stack>
									</Stack>
								</Stack>
								<Stack gap="1">
									<Text fontWeight="semibold">
										<Icons.Support rotate="90" /> Terms Settings
									</Text>
									<Stack
										gap="4"
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Section Title
											</Text>
											<Text>{template.terms_section_settings?.title}</Text>
										</Stack>
									</Stack>
								</Stack>
								<Stack gap="1">
									<Text fontWeight="semibold">
										<Icons.Support rotate="90" /> Inclusions and Exclusions
										Settings
									</Text>
									<Stack
										gap="4"
										padding="4"
										rounded="md"
										bgColor="default"
										borderWidth="1"
									>
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold" color="muted">
												Section Title
											</Text>
											<Text>
												{template.inclusions_exclusions_section_settings.title}
											</Text>
										</Stack>
									</Stack>
								</Stack>
								<Text>
									<b>Settings for Sales Title</b>:{" "}
									{template.hero_section_settings.sales_info_title ? (
										template.hero_section_settings.sales_info_title
									) : (
										<Text as="span" color={"muted"}>
											N/A
										</Text>
									)}
								</Text>
								<Text color="muted" fontSize="sm">
									{joinAttributes(
										template.updated_by && template.updated_at ? (
											<>
												Updated by {template.updated_by.name},{" "}
												<RelativeTime timestamp={template.updated_at} />
											</>
										) : undefined,
										<>
											Created by {template.created_by?.name || "You"},{" "}
											<RelativeTime timestamp={template.created_at} />
										</>
									)}
								</Text>
							</Stack>
						)
					})
				)}
			</Box>
			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="Update Trip Quote PDF Template"
				fitContainer
			>
				<Dialog.Body>
					<EditTripQuotePdfTemplate
						onCancel={() => {
							closeDialog()
						}}
						onSuccess={() => {
							closeDialog()
							mutate()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</Stack>
	)
}
function EditTripQuotePdfTemplate({
	onCancel,
	onSuccess,
}: {
	onCancel: () => void
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const id__ = useId()
	const { data } = useSWR(`/trip-quote-pdf-template/update?${id__}`, () =>
		xhr
			.get<{
				data: TEmailPdfTemplateCreatePayload
				meta: TBrandWiseColors
			}>("/trip-quote-pdf-templates/create")
			.then((resp) => resp.data)
	)
	if (!data) {
		return <Spinner alignCenter padding="4" />
	}
	return (
		<Stack gap="4">
			<EditTripQuotePdfTemplateForm
				initialValues={data.data}
				tenantColor={data.meta}
				onCancel={onCancel}
				onSubmit={async (payload) => {
					const data = {
						...payload,
						section_ordering_settings: payload.section_ordering_settings?.map(
							(section, index) => {
								return {
									section: section.section,
									order: index + 1,
									hide: section.hide,
								}
							}
						),
					}
					await xhr.post("/trip-quote-pdf-templates", data)
					onSuccess()
				}}
			/>
			<Alert>
				These settings will be applied to all future Quote PDFs. You can
				regenerate the existing Quote PDFs to apply these changes as well.
			</Alert>
		</Stack>
	)
}
